/**
 * Inner game NetMessage structs (Step 5a) — the field-level codec for the messages wrapped
 * inside XMSG_GAMENETMESSAGE (0x54). Port of the structs in mpshared/netmessage.h + mpht.h
 * and their field-by-field byte-swap (netmessage.cpp NetMessageByteOrderSwap).
 *
 * Operates on the INNER bytes (what netmessage.ts `decodeGame` exposes as `inner`, i.e. the
 * payload after [0x54][nmid]). decodeInner(nmid, inner) → typed struct; encodeInner(struct) →
 * inner bytes. Combine with netmessage.ts encodeGame to produce a full 0x54 frame.
 *
 * KEY: the relay does NOT interpret command payloads. A `Message` (the 28-byte relayed unit)
 * is carried as an OPAQUE blob — received in network order, re-broadcast in network order, so
 * copying it verbatim is byte-exact (swap-to-native-then-back is identity). Only fields the
 * SERVER reads (GameParams.tGameSpeed, player names) or WRITES (counts, block/sync, seed,
 * player records) are decoded. All multibyte fields are big-endian.
 *
 * Verified byte-exact against captured vectors (test/protocol/gamemsg.test.ts): server-emitted
 * structs (ScGameParams/ScBeginGame/ScPlayersUpdate) round-trip; client structs decode fields.
 */
import { ByteReader, ByteWriter } from './bytebuffer.ts';
import { NMID } from './constants.ts';

// mpht.h `struct Message` under #pragma pack(push,2): header 12B (mid+smidSender+smidReceiver+wDummy
// +int tDelivery) + largest union arm 16B (MoveCommand/AttackCommand: Gid+WPoint+WPoint+TCoord+WCoord
// +wDummy) = 28B. ClientCommandsNetMessage sizes its array with sizeof(Message), so every command on
// the wire is exactly this — NOT 32. (A wrong 32 here underflows the decode of every real command.)
export const MESSAGE_SIZE = 28;
export const GAMEPARAMS_SIZE = 60; // mpht.h struct GameParams

// ── GameParams (mpht.h:117) ──────────────────────────────────────────────────
export interface GameParams {
  packId: number; // packid.id (PACKID_MAIN = 0xffffffff)
  packHash: Uint8Array; // packid.hash[16] (opaque)
  dwVersionSimulation: number;
  tGameSpeed: number; // cmsUpdate = tGameSpeed * 10
  szLvlFilename: string;
  filler: Uint8Array; // 3 bytes (preserved for byte-exactness)
}
export function decodeGameParams(r: ByteReader): GameParams {
  return {
    packId: r.readDword(),
    packHash: r.readBytes(16),
    dwVersionSimulation: r.readDword(),
    tGameSpeed: r.readDword(),
    szLvlFilename: r.readFixedString(29),
    filler: r.readBytes(3),
  };
}
export function encodeGameParams(w: ByteWriter, gp: GameParams): void {
  w.writeDword(gp.packId);
  w.writeBytes(gp.packHash);
  w.writeDword(gp.dwVersionSimulation);
  w.writeDword(gp.tGameSpeed);
  w.writeFixedString(gp.szLvlFilename, 29);
  w.writeBytes(gp.filler);
}

// ── PlayerRecord (netmessage.h, inside ScPlayersUpdate) — 38 bytes ───────────
export interface PlayerRecord {
  pid: number; // word
  side: number; // word
  wf: number; // word (kfPlr* flags)
  name: string; // char[32]
}

// ── CsWinStats (nmid 21) — netmessage.h WinStatsNetMessage + WinStats (pack=2, 116-byte struct) ──
export const WINSTATS_SIZE = 116; // sizeof(WinStats)
export const KUT_MAX = 23; // side.h kutMax — unit/built count array length

/** The decoded WinStats fields (native order). */
export interface WinStatsWire {
  sidm: number;
  sidmAllies: number;
  enemyMobileUnitsKilled: number;
  enemyStructuresKilled: number;
  mobileUnitsLost: number;
  structuresLost: number;
  ff: number;
  acut: number[];
  acutBuilt: number[];
  creditsAcquired: number;
  creditsConsumed: number;
}

/** Decode the WinStatsNetMessage inner payload (after [0x54][21]): pid, filler, the 16-byte MD5
 *  hash, and the 116-byte WinStats — returned as a NATIVE little-endian byte image (the wire is
 *  big-endian; the obfuscation XOR + MD5 run on native bytes, per game/Player.cpp). The returned
 *  `ws` is still obfuscated; the caller XOR-deobfuscates with `hash`, MD5-verifies, then parses. */
export function decodeWinStats(inner: Uint8Array): {
  pid: number;
  filler: number;
  hash: Uint8Array;
  ws: Uint8Array;
} {
  const r = new ByteReader(inner);
  const pid = r.readWord();
  const filler = r.readWord();
  const hash = r.readBytes(16);
  const ws = new Uint8Array(WINSTATS_SIZE);
  const dv = new DataView(ws.buffer);
  for (let i = 0; i < 54; i++) dv.setUint16(i * 2, r.readWord(), true); // 7 + 23 + 23 + 1(dummy) words
  dv.setUint32(108, r.readDword(), true); // cCreditsAcquired
  dv.setUint32(112, r.readDword(), true); // cCreditsConsumed
  return { pid, filler, hash, ws };
}

/** Parse a (deobfuscated) native-LE WinStats byte image into its fields. */
export function parseWinStats(ws: Uint8Array): WinStatsWire {
  const dv = new DataView(ws.buffer, ws.byteOffset, ws.byteLength);
  const u16 = (off: number) => dv.getUint16(off, true);
  return {
    sidm: u16(0),
    sidmAllies: u16(2),
    enemyMobileUnitsKilled: u16(4),
    enemyStructuresKilled: u16(6),
    mobileUnitsLost: u16(8),
    structuresLost: u16(10),
    ff: u16(12),
    acut: Array.from({ length: KUT_MAX }, (_, i) => u16(14 + i * 2)),
    acutBuilt: Array.from({ length: KUT_MAX }, (_, i) => u16(60 + i * 2)),
    creditsAcquired: dv.getUint32(108, true),
    creditsConsumed: dv.getUint32(112, true),
  };
}

// ── Inner game messages, keyed by nmid ───────────────────────────────────────
export type GameInner =
  | { nmid: typeof NMID.ScGameParams; kind: 'gameparams'; params: GameParams }
  | { nmid: typeof NMID.ScBeginGame; kind: 'begingame'; ulRandomSeed: number }
  | { nmid: typeof NMID.CsPlayerJoin; kind: 'playerjoin'; name: string }
  | {
      nmid: typeof NMID.ScPlayersUpdate;
      kind: 'playersupdate';
      cplrr: number;
      players: PlayerRecord[];
    }
  | { nmid: typeof NMID.CsClientCommands; kind: 'clientcommands'; commands: Uint8Array[] }
  | {
      nmid: typeof NMID.ScUpdate;
      kind: 'scupdate';
      cUpdatesBlock: number;
      cUpdatesSync: number;
      commands: Uint8Array[];
    }
  | {
      nmid: typeof NMID.CsUpdateResult;
      kind: 'updateresult';
      cUpdatesBlock: number;
      hash: number;
      cmsLatency: number;
    }
  | { nmid: typeof NMID.ScLagNotify; kind: 'lagnotify'; pidLagging: number; cSeconds: number }
  | { nmid: number; kind: 'empty' } // CsClientReady(7), CsRequestBeginGame(16), …
  | { nmid: number; kind: 'rawinner'; bytes: Uint8Array }; // not yet field-decoded → opaque

/** Decode inner bytes (after [0x54][nmid]) into a typed struct. */
export function decodeInner(nmid: number, inner: Uint8Array): GameInner {
  const r = new ByteReader(inner);
  switch (nmid) {
    case NMID.ScGameParams:
      return { nmid, kind: 'gameparams', params: decodeGameParams(r) };
    case NMID.ScBeginGame:
      return { nmid, kind: 'begingame', ulRandomSeed: r.readDword() };
    case NMID.CsPlayerJoin:
      return { nmid, kind: 'playerjoin', name: r.readFixedString(32) };
    case NMID.ScPlayersUpdate: {
      const cplrr = r.readWord();
      r.readWord(); // wDummy
      const players: PlayerRecord[] = [];
      while (r.remaining >= 38) {
        players.push({
          pid: r.readWord(),
          side: r.readWord(),
          wf: r.readWord(),
          name: r.readFixedString(32),
        });
      }
      return { nmid, kind: 'playersupdate', cplrr, players };
    }
    case NMID.CsClientCommands: {
      const count = r.readWord();
      r.readWord(); // wDummy
      const commands: Uint8Array[] = [];
      for (let i = 0; i < count; i++) commands.push(r.readBytes(MESSAGE_SIZE));
      return { nmid, kind: 'clientcommands', commands };
    }
    case NMID.ScUpdate: {
      const cUpdatesBlock = r.readDword();
      const cUpdatesSync = r.readDword();
      const count = r.readWord();
      r.readWord(); // wDummy
      const commands: Uint8Array[] = [];
      for (let i = 0; i < count; i++) commands.push(r.readBytes(MESSAGE_SIZE));
      return { nmid, kind: 'scupdate', cUpdatesBlock, cUpdatesSync, commands };
    }
    case NMID.CsUpdateResult:
      return {
        nmid,
        kind: 'updateresult',
        cUpdatesBlock: r.readDword(),
        hash: r.readDword(),
        cmsLatency: r.readDword() | 0,
      };
    case NMID.ScLagNotify:
      return { nmid, kind: 'lagnotify', pidLagging: r.readWord(), cSeconds: r.readWord() };
    case NMID.CsClientReady:
    case NMID.CsRequestBeginGame:
      return { nmid, kind: 'empty' };
    default:
      return { nmid, kind: 'rawinner', bytes: inner };
  }
}

/** Encode a typed struct back to inner bytes. */
export function encodeInner(msg: GameInner): Uint8Array {
  const w = new ByteWriter();
  switch (msg.kind) {
    case 'gameparams':
      encodeGameParams(w, msg.params);
      break;
    case 'begingame':
      w.writeDword(msg.ulRandomSeed);
      break;
    case 'playerjoin':
      w.writeFixedString(msg.name, 32);
      break;
    case 'playersupdate':
      w.writeWord(msg.cplrr);
      w.writeWord(0); // wDummy
      for (const p of msg.players) {
        w.writeWord(p.pid);
        w.writeWord(p.side);
        w.writeWord(p.wf);
        w.writeFixedString(p.name, 32);
      }
      break;
    case 'clientcommands':
      w.writeWord(msg.commands.length);
      w.writeWord(0); // wDummy
      for (const c of msg.commands) w.writeBytes(c);
      break;
    case 'scupdate':
      w.writeDword(msg.cUpdatesBlock);
      w.writeDword(msg.cUpdatesSync);
      w.writeWord(msg.commands.length);
      w.writeWord(0); // wDummy
      for (const c of msg.commands) w.writeBytes(c);
      break;
    case 'updateresult':
      w.writeDword(msg.cUpdatesBlock);
      w.writeDword(msg.hash);
      w.writeDword(msg.cmsLatency >>> 0);
      break;
    case 'lagnotify':
      w.writeWord(msg.pidLagging);
      w.writeWord(msg.cSeconds);
      break;
    case 'empty':
      break;
    case 'rawinner':
      w.writeBytes(msg.bytes);
      break;
  }
  return w.toUint8Array();
}
